import { useEffect, useMemo, useState } from 'react'

import { getAllChannelsList } from '@/services/channels'
import {
  IDP_SITE_OPTIONS,
  initialFilterSiteFromSearch,
  normalizeFilterSite,
  siteFiltersForSaleChannels,
  siteOptionsForFilters,
} from './siteWebKeys'

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function loadChannelsWithRetry(attempt = 0): Promise<any> {
  return getAllChannelsList({}).catch(error => {
    if (attempt >= 2) throw error
    return sleep(300 * (attempt + 1)).then(() => loadChannelsWithRetry(attempt + 1))
  })
}

function initialSiteFromLocation(initialSite: string) {
  if (typeof window === 'undefined') return normalizeFilterSite(initialSite)
  return initialFilterSiteFromSearch(window.location.search, initialSite)
}

export function useIdpSiteAccess(initialSite = 'all') {
  const [filterSite, setFilterSite] = useState<string>(() => initialSiteFromLocation(initialSite))
  const [allowedSiteFilters, setAllowedSiteFilters] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(undefined)
    loadChannelsWithRetry()
      .then((channels: any) => {
        if (cancelled) return
        const filters = siteFiltersForSaleChannels(channels)
        setAllowedSiteFilters(filters)
        setFilterSite(current => normalizeFilterSite(current, filters))
      })
      .catch((err: any) => {
        if (cancelled) return
        setAllowedSiteFilters([])
        setFilterSite('')
        setError(err?.message || 'sale_channel permission API unavailable')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (loading) return
    setFilterSite(current => normalizeFilterSite(current, allowedSiteFilters))
  }, [loading, allowedSiteFilters.join(',')])

  const siteOptions = useMemo(() => siteOptionsForFilters(allowedSiteFilters), [allowedSiteFilters.join(',')])

  return {
    filterSite,
    setFilterSite,
    allowedSiteFilters,
    siteOptions,
    loading,
    error,
    hasAccess: allowedSiteFilters.length > 0,
    allSiteOptions: IDP_SITE_OPTIONS,
  }
}
